/**
 * Format glass side labels for display (main glass type + optional patterned subtype).
 * Mirrors backend apps/orders/glass_display.py and GlassValueDisplay rules.
 */
import type { OrderSummary } from './types';

export type GlassSide = 'one' | 'two' | 'three';

type LabelMap = Map<string, string> | Record<string, string> | undefined;

type SideConfig = {
  valueIdKeys: readonly string[];
  patternOptionKey: string;
  wooGlassKey: 'side_one' | 'side_two' | 'side_three';
  wooPatternKey: 'one' | 'two' | 'three';
  apiDisplayKey: 'glass_side_one_display' | 'glass_side_two_display' | 'middle_glass_panel_display';
};

const SIDE_CONFIG: Record<GlassSide, SideConfig> = {
  one: {
    valueIdKeys: ['glass_side_one_value_id'],
    patternOptionKey: 'patterned_gs_one_option_id',
    wooGlassKey: 'side_one',
    wooPatternKey: 'one',
    apiDisplayKey: 'glass_side_one_display',
  },
  two: {
    valueIdKeys: ['glass_side_two_value_id'],
    patternOptionKey: 'patterned_gs_two_option_id',
    wooGlassKey: 'side_two',
    wooPatternKey: 'two',
    apiDisplayKey: 'glass_side_two_display',
  },
  three: {
    valueIdKeys: ['middle_glass_panel_value_id', 'glass_side_three_value_id'],
    patternOptionKey: 'patterned_gs_three_option_id',
    wooGlassKey: 'side_three',
    wooPatternKey: 'three',
    apiDisplayKey: 'middle_glass_panel_display',
  },
};

export type FormatGlassSideDisplayInput = {
  metadata?: OrderSummary['metadata'] | Record<string, unknown> | null;
  woo_glass_raw?: OrderSummary['woo_glass_raw'] | Record<string, unknown> | null;
  glass_side_one_display?: string | null;
  glass_side_two_display?: string | null;
  middle_glass_panel_display?: string | null;
};

function trim(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function lookupLabel(map: LabelMap, id: string): string {
  if (!map || !id) return '';
  if (map instanceof Map) return trim(map.get(id));
  return trim(map[id]);
}

function joinMainAndPattern(main: string, pattern: string): string {
  const m = main.trim();
  const p = pattern.trim();
  if (m && p) return `${m}, ${p}`;
  return m || p || '';
}

/** Join resolved main glass label and pattern label for display. */
export function formatGlassParts(main: string, pattern: string): string {
  const joined = joinMainAndPattern(main, pattern);
  return joined || '-';
}

function metadataRecord(order: FormatGlassSideDisplayInput): Record<string, unknown> {
  const meta = order.metadata;
  return meta && typeof meta === 'object' ? (meta as Record<string, unknown>) : {};
}

function resolveValueId(meta: Record<string, unknown>, side: GlassSide): string {
  for (const key of SIDE_CONFIG[side].valueIdKeys) {
    const raw = trim(meta[key]);
    if (raw) return raw;
  }
  return '';
}

function wooPatternFromMeta(meta: Record<string, unknown>, side: GlassSide): string {
  const wooPatterned = meta.woo_patterned_glass;
  if (!wooPatterned || typeof wooPatterned !== 'object') return '';
  return trim((wooPatterned as Record<string, unknown>)[SIDE_CONFIG[side].wooPatternKey]);
}

function wooGlassFromOrder(order: FormatGlassSideDisplayInput, side: GlassSide): string {
  const wooGlass =
    order.woo_glass_raw ??
    (metadataRecord(order).woo_glass_raw as Record<string, unknown> | undefined);
  if (!wooGlass || typeof wooGlass !== 'object') return '';
  return trim(wooGlass[SIDE_CONFIG[side].wooGlassKey]);
}

function apiDisplayFromOrder(order: FormatGlassSideDisplayInput, side: GlassSide): string {
  const key = SIDE_CONFIG[side].apiDisplayKey;
  return trim((order as Record<string, unknown>)[key]);
}

function hasWooPattern(meta: Record<string, unknown>, side: GlassSide): boolean {
  return !!wooPatternFromMeta(meta, side);
}

export function formatGlassSideDisplay(
  order: FormatGlassSideDisplayInput,
  side: GlassSide,
  options?: {
    glassLabelMap?: LabelMap;
    patternLabelMap?: LabelMap;
    /** When true, use API *_display if it already includes the pattern or no woo pattern exists. */
    preferApiDisplay?: boolean;
  }
): string {
  const cfg = SIDE_CONFIG[side];
  const meta = metadataRecord(order);
  const preferApi = options?.preferApiDisplay !== false;
  const apiDisplay = apiDisplayFromOrder(order, side);
  const wooPattern = wooPatternFromMeta(meta, side);

  if (preferApi && apiDisplay) {
    const needsCompose = wooPattern && !apiDisplay.includes(',');
    if (!needsCompose) return apiDisplay;
  }

  let main = '';
  const valueId = resolveValueId(meta, side);
  if (valueId) {
    main = lookupLabel(options?.glassLabelMap, valueId);
  }
  if (!main) {
    main = wooGlassFromOrder(order, side);
  }

  let pattern = '';
  const patternId = trim(meta[cfg.patternOptionKey]);
  if (patternId) {
    pattern = lookupLabel(options?.patternLabelMap, patternId);
  }
  if (!pattern) {
    pattern = wooPattern;
  }

  const composed = joinMainAndPattern(main, pattern);
  if (composed) return composed;
  return apiDisplay || wooGlassFromOrder(order, side);
}

export function glassSideFromDisplayColumnKey(
  columnKey: string
): GlassSide | null {
  if (columnKey === 'glass_side_one_display') return 'one';
  if (columnKey === 'glass_side_two_display') return 'two';
  if (columnKey === 'middle_glass_panel_display') return 'three';
  return null;
}

export function glassSideFromValueIdKey(columnKey: string): GlassSide | null {
  if (columnKey.includes('glass_side_one') || columnKey.endsWith('glass_side_one_value_id')) return 'one';
  if (columnKey.includes('glass_side_two') || columnKey.endsWith('glass_side_two_value_id')) return 'two';
  if (
    columnKey.includes('middle_glass_panel') ||
    columnKey.includes('glass_side_three')
  ) {
    return 'three';
  }
  return null;
}
